import apiClient, { generateSessionId } from './apiClient';
import { AUTH_CONFIG } from './config';

// Use apiClient instead of direct axios instance
const api = apiClient;


// Auth service
const authService = {
  // Register a new tenant with admin user
  registerTenant: async (tenantData) => {
    try {
      const response = await api.post('/auth/register-tenant', tenantData);
      if (response.data.token) {
        // Generate a new session ID for this login
        const sessionId = generateSessionId();
        localStorage.setItem(AUTH_CONFIG.SESSION_KEY, sessionId);

        localStorage.setItem(AUTH_CONFIG.TOKEN_KEY, response.data.token);
        localStorage.setItem(AUTH_CONFIG.USER_KEY, JSON.stringify(response.data.user));
        localStorage.setItem(AUTH_CONFIG.TENANT_KEY, JSON.stringify(response.data.tenant));
      }
      return response.data;
    } catch (error) {
      console.error('Registration error:', error);
      throw error.response?.data || { message: 'Registration failed' };
    }
  },

  // Register a new user within a tenant (admin only)
  registerUser: async (userData) => {
    try {
      const response = await api.post('/auth/register', userData);
      return response.data;
    } catch (error) {
      console.error('User registration error:', error);
      throw error.response?.data || { message: 'User registration failed' };
    }
  },

  // Login user
  login: async (credentials) => {
    try {
      // Generate a new session ID for this login attempt
      const sessionId = generateSessionId();
      localStorage.setItem(AUTH_CONFIG.SESSION_KEY, sessionId);

      // Include session ID in login request
      const response = await api.post('/auth/login', {
        ...credentials,
        sessionId
      });

      if (response.data.token) {
        if (response.data.tenant && response.data.subscription) {
          response.data.tenant.isPhase2Compliant = response.data.subscription.isPhase2Compliant === true;
        }

        localStorage.setItem(AUTH_CONFIG.TOKEN_KEY, response.data.token);
        localStorage.setItem(AUTH_CONFIG.USER_KEY, JSON.stringify(response.data.user));
        localStorage.setItem(AUTH_CONFIG.TENANT_KEY, JSON.stringify(response.data.tenant));

        // Console log the token for debugging
        console.log('Login successful. Token:', response.data.token);
        console.log('User data:', response.data.user);
        console.log('Tenant data:', response.data.tenant);
      }
      return response.data;
    } catch (error) {
      console.error('Login error:', error);
      // Clear any previous session data on failed login
      localStorage.removeItem(AUTH_CONFIG.TOKEN_KEY);

      // Properly format the error to be handled by the UI
      if (error.response) {
        throw {
          status: error.response.status,
          message: error.response.data.message || 'Invalid credentials',
          response: error.response
        };
      } else {
        throw { message: error.message || 'Login failed. Please try again.' };
      }
    }
  },

  // Firebase login with Google
  firebaseLogin: async (userData) => {
    try {
      const sessionId = generateSessionId();
      localStorage.setItem(AUTH_CONFIG.SESSION_KEY, sessionId);

      console.log('Firebase auth data:', userData);

      const response = await api.post('/auth/firebase-login', {
        ...userData,
        sessionId
      });

      const { token, user, tenant, subscription } = response.data;

      if (tenant && subscription) {
        tenant.isPhase2Compliant = subscription.isPhase2Compliant === true;
      }

      // Store auth data in localStorage
      localStorage.setItem(AUTH_CONFIG.TOKEN_KEY, token);
      localStorage.setItem(AUTH_CONFIG.USER_KEY, JSON.stringify(user));
      localStorage.setItem(AUTH_CONFIG.TENANT_KEY, JSON.stringify(tenant));

      // Console log the token for debugging
      console.log('Firebase login successful. Token:', token);
      console.log('User data:', user);
      console.log('Tenant data:', tenant);

      return response.data;
    } catch (error) {
      console.error('Firebase login error:', error);
      throw error.response?.data || { message: 'Firebase login failed' };
    }
  },

  // Get current user profile
  getCurrentUser: async () => {
    try {
      const response = await api.get('/auth/me');
      if (response.data.tenant && response.data.subscription) {
        response.data.tenant.isPhase2Compliant = response.data.subscription.isPhase2Compliant === true;
      }
      return response.data;
    } catch (error) {
      console.error('Error fetching user profile:', error);
      throw error.response?.data || { message: 'Error fetching user profile' };
    }
  },

  // Logout user
  logout: async () => {
    try {
      // Notify the server about logout
      await api.post('/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear local storage regardless of server response
      localStorage.removeItem(AUTH_CONFIG.TOKEN_KEY);
      localStorage.removeItem(AUTH_CONFIG.USER_KEY);
      localStorage.removeItem(AUTH_CONFIG.TENANT_KEY);
      localStorage.removeItem(AUTH_CONFIG.SESSION_KEY);
    }
  },

  // Check if user is logged in
  isLoggedIn: () => {
    return !!localStorage.getItem(AUTH_CONFIG.TOKEN_KEY);
  },

  // Get current user from local storage
  getUser: () => {
    const user = localStorage.getItem(AUTH_CONFIG.USER_KEY);
    return user ? JSON.parse(user) : null;
  },

  // Get current tenant from local storage
  getTenant: () => {
    const tenant = localStorage.getItem(AUTH_CONFIG.TENANT_KEY);
    return tenant ? JSON.parse(tenant) : null;
  }
};

const productService = {
  getProducts: async (page = 1, limit = 10, search = '', itemType = '') => {
    try {
      // Use the configured api instance with authentication
      const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
      const typeParam = itemType ? `&itemType=${encodeURIComponent(itemType)}` : '';
      const response = await api.get(`/products?page=${page}&limit=${limit}${searchParam}${typeParam}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching products:', error);
      throw error;
    }
  },

  createProduct: async (productData) => {
    try {
      // Use the configured api instance with authentication
      const response = await api.post(`/products`, productData);
      return response.data;
    } catch (error) {
      console.error('Error creating product:', error);
      throw error;
    }
  },

  updateProduct: async (id, productData) => {
    try {
      // Use the configured api instance with authentication
      const response = await api.put(`/products/${id}`, productData);
      return response.data;
    } catch (error) {
      console.error('Error updating product:', error);
      throw error;
    }
  },

  getProduct: async (id) => {
    try {
      // Use the configured api instance with authentication
      const response = await api.get(`/products/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching product:', error);
      throw error;
    }
  },

  deleteProduct: async (id) => {
    try {
      // Use the configured api instance with authentication
      const response = await api.delete(`/products/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting product:', error);
      throw error;
    }
  },

  downloadBulkSample: async () => {
    try {
      const response = await api.get('/products/bulk/sample', {
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      console.error('Error downloading bulk sample:', error);
      throw error;
    }
  },

  getBulkFields: async () => {
    try {
      const response = await api.get('/products/bulk/fields');
      return response.data;
    } catch (error) {
      console.error('Error fetching bulk fields:', error);
      throw error;
    }
  },

  /**
   * POST /products/bulk-upload
   * Sends the raw Excel file as multipart/form-data.
   * @param {File}   file         - The original .xlsx / .xls file object
   * @param {Object} fieldMap - { apiField: excelColumnHeader } mapping chosen by the user
   */
  bulkImport: async (file, fieldMap = {}) => {
    try {
      const formData = new FormData();
      formData.append('file', file, file.name);
      // Send the mapping so the server can rename columns if it supports it
      formData.append('fieldMap', JSON.stringify(fieldMap));

      const response = await api.post('/products/bulk-upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    } catch (error) {
      console.error('Error bulk importing products:', error);
      const msg =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        'Bulk import failed';
      const customError = new Error(msg);
      let errors =
        error?.response?.data?.failedRecords ||
        error?.response?.data?.errors;
      if (Array.isArray(errors)) {
        customError.failedRecords = errors.map((e) => ({
          ...e,
          row: e.row ? e.row - 1 : e.row,
        }));
      }
      throw customError;
    }
  }
};

// Customer Service
const customerService = {
  getCustomers: async (page = 1, limit = 10, search = '', customerType = '') => {
    try {
      const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
      const typeParam = customerType ? `&customerType=${encodeURIComponent(customerType)}` : '';
      const response = await api.get(`/customers?page=${page}&limit=${limit}${searchParam}${typeParam}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching customers:', error);
      throw error.response?.data || { message: 'Error fetching customers' };
    }
  },

  getCustomer: async (id) => {
    try {
      const response = await api.get(`/customers/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching customer:', error);
      throw error.response?.data || { message: 'Error fetching customer' };
    }
  },

  createCustomer: async (customerData) => {
    try {
      const response = await api.post('/customers', customerData);
      return response.data;
    } catch (error) {
      console.error('Error creating customer:', error);
      throw error.response?.data || { message: 'Error creating customer' };
    }
  },

  updateCustomer: async (id, customerData) => {
    try {
      const response = await api.put(`/customers/${id}`, customerData);
      return response.data;
    } catch (error) {
      console.error('Error updating customer:', error);
      throw error.response?.data || { message: 'Error updating customer' };
    }
  },

  deleteCustomer: async (id) => {
    try {
      const response = await api.delete(`/customers/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting customer:', error);
      throw error.response?.data || { message: 'Error deleting customer' };
    }
  },

  downloadBulkSample: async () => {
    try {
      const response = await api.get('/customers/bulk/sample', {
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      console.error('Error downloading bulk sample:', error);
      throw error;
    }
  },

  getBulkFields: async () => {
    try {
      const response = await api.get('/customers/bulk/fields');
      return response.data;
    } catch (error) {
      console.error('Error fetching bulk fields:', error);
      throw error;
    }
  },

  /**
   * POST /customers/bulk-upload
   * Sends the raw Excel file as multipart/form-data.
   * @param {File}   file         - The original .xlsx / .xls file object
   * @param {Object} fieldMap - { apiField: excelColumnHeader } mapping chosen by the user
   */
  bulkImport: async (file, fieldMap = {}) => {
    try {
      const formData = new FormData();
      formData.append('file', file, file.name);
      formData.append('fieldMap', JSON.stringify(fieldMap));

      const response = await api.post('/customers/bulk-upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    } catch (error) {
      console.error('Error bulk importing customers:', error);
      const msg =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        'Bulk import failed';
      const customError = new Error(msg);
      let errors =
        error?.response?.data?.failedRecords ||
        error?.response?.data?.errors ||
        [];

      // 🔥 normalize + fix row index
      customError.failedRecords = Array.isArray(errors)
        ? errors.map((e) => ({
          ...e,

          // adjust header offset (most common fix)
          row: e.row ? e.row - 1 : e.row,

          // UI-friendly row
          displayRow: e.row ? e.row - 1 : e.row,
        }))
        : [];
      throw customError;
    }
  }
};

// Supplier service
const supplierService = {
  // Get all suppliers with pagination
  getSuppliers: async (page = 1, limit = 10, search = '') => {
    try {
      const response = await api.get('/suppliers', {
        params: { page, limit, search }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching suppliers:', error);
      throw error.response?.data || { message: 'Error fetching suppliers' };
    }
  },

  // Get single supplier by ID
  getSupplier: async (id) => {
    try {
      const response = await api.get(`/suppliers/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching supplier:', error);
      throw error.response?.data || { message: 'Error fetching supplier' };
    }
  },

  // Create new supplier
  createSupplier: async (supplierData) => {
    try {
      const response = await api.post('/suppliers', supplierData);
      return response.data;
    } catch (error) {
      console.error('Error creating supplier:', error);

      // Check if we have detailed validation errors
      if (error.response?.data?.errors) {
        const validationError = new Error(error.response.data.message || 'Validation failed');
        validationError.validationErrors = error.response.data.errors;
        throw validationError;
      }

      throw error.response?.data || { message: 'Error creating supplier' };
    }
  },

  // Update supplier
  updateSupplier: async (id, supplierData) => {
    try {
      const response = await api.put(`/suppliers/${id}`, supplierData);
      return response.data;
    } catch (error) {
      console.error('Error updating supplier:', error);

      // Check if we have detailed validation errors
      if (error.response?.data?.errors) {
        const validationError = new Error(error.response.data.message || 'Validation failed');
        validationError.validationErrors = error.response.data.errors;
        throw validationError;
      }

      throw error.response?.data || { message: 'Error updating supplier' };
    }
  },

  // Delete supplier
  deleteSupplier: async (id) => {
    try {
      const response = await api.delete(`/suppliers/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting supplier:', error);
      throw error.response?.data || { message: 'Error deleting supplier' };
    }
  },

  downloadBulkSample: async () => {
    try {
      const response = await api.get('/suppliers/bulk/sample', {
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      console.error('Error downloading bulk sample:', error);
      throw error;
    }
  },

  getBulkFields: async () => {
    try {
      const response = await api.get('/suppliers/bulk/fields');
      return response.data;
    } catch (error) {
      console.error('Error fetching bulk fields:', error);
      throw error;
    }
  },

  /**
   * POST /suppliers/bulk-upload
   * Sends the raw Excel file as multipart/form-data.
   * @param {File}   file         - The original .xlsx / .xls file object
   * @param {Object} fieldMap - { apiField: excelColumnHeader } mapping chosen by the user
   */
  bulkImport: async (file, fieldMap = {}) => {
    try {
      const formData = new FormData();
      formData.append('file', file, file.name);
      formData.append('fieldMap', JSON.stringify(fieldMap));

      const response = await api.post('/suppliers/bulk-upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    } catch (error) {
      console.error('Error bulk importing suppliers:', error);
      const msg =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        'Bulk import failed';
      const customError = new Error(msg);
      let errors =
        error?.response?.data?.failedRecords ||
        error?.response?.data?.errors ||
        [];
      customError.failedRecords = Array.isArray(errors)
        ? errors.map((e) => ({
          ...e,

          row: e.row ? e.row - 1 : e.row,
          displayRow: e.row ? e.row - 1 : e.row,
        }))
        : [];
      throw customError;
    }
  }
};

// Invitation service
const invitationService = {
  // Get all invitations for current tenant
  getInvitations: async (page = 1, limit = 10, search = '') => {
    try {
      const response = await api.get('/invitations', {
        params: { page, limit, search }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching invitations:', error);
      throw error.response?.data || { message: 'Error fetching invitations' };
    }
  },

  // Create a new invitation
  createInvitation: async (data) => {
    try {
      const response = await api.post('/invitations', {
        email: data.email,
        role: data.role,
        permissions: data.permissions
      });
      return response.data;
    } catch (error) {
      console.error('Error creating invitation:', error);
      throw error.response?.data || { message: 'Error creating invitation' };
    }
  },

  // Resend an invitation
  resendInvitation: async (invitationId) => {
    try {
      const response = await api.post(`/invitations/${invitationId}/resend`);
      return response.data;
    } catch (error) {
      console.error('Error resending invitation:', error);
      throw error.response?.data || { message: 'Error resending invitation' };
    }
  },

  // Cancel an invitation
  cancelInvitation: async (invitationId) => {
    try {
      const response = await api.post(`/invitations/${invitationId}/cancel`);
      return response.data;
    } catch (error) {
      console.error('Error cancelling invitation:', error);
      throw error.response?.data || { message: 'Error cancelling invitation' };
    }
  },

  // Verify invitation token
  verifyInvitation: async (token) => {
    try {
      const response = await api.get(`/invitations/verify/${token}`);
      return response.data;
    } catch (error) {
      console.error('Error verifying invitation:', error);
      throw error.response?.data || { message: 'Error verifying invitation' };
    }
  },

  // Accept invitation
  acceptInvitation: async (token, userData) => {
    try {
      const response = await api.post(`/invitations/accept/${token}`, userData);
      return response.data;
    } catch (error) {
      console.error('Error accepting invitation:', error);
      throw error.response?.data || { message: 'Error accepting invitation' };
    }
  }
};

// Draft Invoice service
const draftInvoiceService = {
  // Create a new draft invoice
  createDraftInvoice: async (draftData) => {
    try {
      const response = await api.post('/draft-invoices', draftData);
      return response.data;
    } catch (error) {
      console.error('Error creating draft invoice:', error);
      throw error.response?.data || { message: 'Error creating draft invoice' };
    }
  },

  // Update an existing draft invoice
  updateDraftInvoice: async (invoiceNo, draftData) => {
    try {
      const response = await api.put(`/draft-invoices/${invoiceNo}`, draftData);
      return response.data;
    } catch (error) {
      console.error('Error updating draft invoice:', error);
      throw error.response?.data || { message: 'Error updating draft invoice' };
    }
  },

  // Get all draft invoices with pagination
  getDraftInvoices: async (page = 1, limit = 10) => {
    try {
      const response = await api.get(`/draft-invoices?page=${page}&limit=${limit}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching draft invoices:', error);
      throw error.response?.data || { message: 'Error fetching draft invoices' };
    }
  },

  // Get a single draft invoice by invoice number
  getDraftInvoice: async (invoiceNo) => {
    try {
      const response = await api.get(`/draft-invoices/${invoiceNo}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching draft invoice:', error);
      throw error.response?.data || { message: 'Error fetching draft invoice' };
    }
  },

  // Convert a draft invoice to a regular invoice
  convertDraftToInvoice: async (invoiceNo) => {
    try {
      const response = await api.post(`/draft-invoices/${invoiceNo}/convert`);
      return response.data;
    } catch (error) {
      console.error('Error converting draft invoice:', error);
      throw error.response?.data || { message: 'Error converting draft invoice' };
    }
  }
};

// Purchase service
const purchaseService = {
  // Get all purchases with pagination
  getPurchases: async (page = 1, limit = 10, search = '') => {
    try {
      const response = await api.get('/purchases', {
        params: { page, limit, search }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching purchases:', error);
      throw error;
    }
  },

  getPurchaseById: async (id) => {
    try {
      const response = await api.get(`/purchases/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching purchase details:', error);
      throw error;
    }
  },

  createPurchase: async (purchaseData) => {
    try {
      const response = await api.post('/purchases', purchaseData);
      return response.data;
    } catch (error) {
      console.error('Error creating purchase:', error);
      throw error;
    }
  },

  updatePurchase: async (id, purchaseData) => {
    try {
      const response = await api.put(`/purchases/${id}`, purchaseData);
      return response.data;
    } catch (error) {
      console.error('Error updating purchase:', error);
      throw error;
    }
  },

  deletePurchase: async (id) => {
    try {
      const response = await api.delete(`/purchases/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting purchase:', error);
      throw error;
    }
  },

  changePurchaseStatus: async (id, status) => {
    try {
      const response = await api.patch(`/purchases/${id}/status`, { status });
      return response.data;
    } catch (error) {
      console.error('Error changing purchase status:', error);
      throw error;
    }
  },

  getNextPurchaseNumber: async () => {
    try {
      const response = await api.get('/sequence/purchase-number');
      return response.data;
    } catch (error) {
      console.error('Error fetching next purchase number:', error);
      throw error;
    }
  }
};

// Purchase Order service
const purchaseOrderService = {
  // Get all purchase orders
  getPurchaseOrders: async () => {
    try {
      const response = await api.get('/purchase-orders');
      return response.data;
    } catch (error) {
      console.error('Error fetching purchase orders:', error);
      throw error;
    }
  },

  getPurchaseOrderById: async (id) => {
    try {
      const response = await api.get(`/purchase-orders/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching purchase order details:', error);
      throw error;
    }
  },

  createPurchaseOrder: async (orderData) => {
    try {
      const response = await api.post('/purchase-orders', orderData);
      return response.data;
    } catch (error) {
      console.error('Error creating purchase order:', error);
      throw error;
    }
  },

  updatePurchaseOrder: async (id, orderData) => {
    try {
      const response = await api.put(`/purchase-orders/${id}`, orderData);
      return response.data;
    } catch (error) {
      console.error('Error updating purchase order:', error);
      throw error;
    }
  },

  deletePurchaseOrder: async (id) => {
    try {
      const response = await api.delete(`/purchase-orders/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting purchase order:', error);
      throw error;
    }
  },

  changePurchaseOrderStatus: async (id, status) => {
    try {
      const response = await api.patch(`/purchase-orders/${id}/status`, { status });
      return response.data;
    } catch (error) {
      console.error('Error changing purchase order status:', error);
      throw error;
    }
  }
};

// Purchase Return service
const purchaseReturnService = {
  // Get all purchase returns
  getPurchaseReturns: async (params = {}) => {
    try {
      const response = await api.get('/purchase-returns', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching purchase returns:', error);
      throw error;
    }
  },

  getPurchaseReturnByInvoiceNo: async (invoiceNo) => {
    try {
      const response = await api.get(`/purchase-returns/${invoiceNo}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching purchase return details:', error);
      throw error;
    }
  },

  createPurchaseReturn: async (returnData) => {
    try {
      const response = await api.post('/purchase-returns', returnData);
      return response.data;
    } catch (error) {
      console.error('Error creating purchase return:', error);
      throw error;
    }
  },

  updatePurchaseReturn: async (id, returnData) => {
    try {
      const response = await api.put(`/purchase-returns/${id}`, returnData);
      return response.data;
    } catch (error) {
      console.error('Error updating purchase return:', error);
      throw error;
    }
  },

  deletePurchaseReturn: async (id) => {
    try {
      const response = await api.delete(`/purchase-returns/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting purchase return:', error);
      throw error;
    }
  },

  changePurchaseReturnStatus: async (id, status) => {
    try {
      const response = await api.patch(`/purchase-returns/${id}/status`, { status });
      return response.data;
    } catch (error) {
      console.error('Error changing purchase return status:', error);
      throw error;
    }
  }
};

// Masters service
const mastersService = {
  // Get all master data for current tenant
  getAllMasters: async (tenantId) => {
    try {
      const response = await api.get(`/masters/${tenantId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching masters:', error);
      throw error.response?.data || { message: 'Error fetching masters' };
    }
  },

  // Create a new master item
  createMaster: async (masterType, tenantId, data) => {
    try {
      const response = await api.post(`/masters/${masterType}/${tenantId}`, data);
      return response.data;
    } catch (error) {
      console.error('Error creating master item:', error);
      throw error.response?.data || { message: 'Error creating master item' };
    }
  },

  // Update a master item
  updateMaster: async (masterType, tenantId, id, data) => {
    try {
      const response = await api.put(`/masters/${masterType}/${tenantId}/${id}`, data);
      return response.data;
    } catch (error) {
      console.error('Error updating master item:', error);
      throw error.response?.data || { message: 'Error updating master item' };
    }
  },

  // Delete a master item
  deleteMaster: async (masterType, tenantId, id) => {
    try {
      const response = await api.delete(`/masters/${masterType}/${tenantId}/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting master item:', error);
      throw error.response?.data || { message: 'Error deleting master item' };
    }
  }
};

export {
  api,
  authService,
  customerService,
  productService,
  supplierService,
  invitationService,
  purchaseService,
  purchaseOrderService,
  mastersService,
  draftInvoiceService,
  purchaseReturnService
};